import { useEffect, useCallback } from 'react';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';
import { useNotificationStore } from '@/stores/notificationStore';
import { updatePushNotificationStatus } from '@/services/data/userTracking';
import { useAuthStore } from '@/stores/authStore';
import { requestOneSignalPushPermission } from '@/services/oneSignal';

export function usePushNotifications() {
  const {
    registrationToken,
    registrationError,
    isRegistered,
    setRegistrationToken,
    setRegistrationError,
    setIsRegistered,
  } = useNotificationStore();

  // Check current permission status
  const checkPermissions = useCallback(async (): Promise<'granted' | 'denied' | 'prompt' | null> => {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      const result = await PushNotifications.checkPermissions();
      const state = result.receive;
      // Map Capacitor PermissionState to our expected types
      if (state === 'granted') return 'granted';
      if (state === 'denied') return 'denied';
      if (state === 'prompt' || state === 'prompt-with-rationale') return 'prompt';
      return null;
    } catch (error) {
      logger.error('Error checking push permissions:', error);
      return null;
    }
  }, []);

  // Request permission and register for push notifications
  const registerNotifications = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      logger.debug('Push notifications not available on web platform');
      // Web = push disabled by definition
      const userId = useAuthStore.getState().userId;
      if (userId) {
        updatePushNotificationStatus(userId, false);
      }
      return;
    }

    try {
      await requestOneSignalPushPermission(false);
      const permission = await PushNotifications.requestPermissions();
      const userId = useAuthStore.getState().userId;

      if (permission.receive === 'granted') {
        logger.info('Notification permission granted');
        setRegistrationError(null);
        
        // Register with APNs/FCM
        await PushNotifications.register();

        // Track push enabled
        if (userId) {
          updatePushNotificationStatus(userId, true);
        }
      } else {
        const errorMessage = 'User denied notification permissions';
        logger.error('Notification permission denied');
        setRegistrationError(errorMessage);
        setIsRegistered(false);

        // Track push disabled
        if (userId) {
          updatePushNotificationStatus(userId, false);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Error registering push notifications:', errorMessage);
      setRegistrationError(errorMessage);
      setIsRegistered(false);
    }
  }, [setRegistrationError, setIsRegistered]);

  // Get delivered notifications
  const getDeliveredNotifications = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      logger.debug('Push notifications not available on web platform');
      return null;
    }

    try {
      const result = await PushNotifications.getDeliveredNotifications();
      return result.notifications;
    } catch (error) {
      logger.error('Error getting delivered notifications:', error);
      return null;
    }
  }, []);

  // Set up listeners on mount
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      logger.debug('Push notifications not available on web platform');
      return;
    }

    // Registration success - receive the device token
    PushNotifications.addListener('registration', (token: Token) => {
      logger.info('Push registration token received:', token.value);
      setRegistrationToken(token.value);
      setIsRegistered(true);
      setRegistrationError(null);
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error) => {
      logger.error('Push registration error:', error.error);
      setRegistrationError(error.error);
      setIsRegistered(false);
    });

    // Notification received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      logger.debug('Push notification received:', notification);
    });

    // User tapped on notification
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      logger.debug('Push notification action performed:', action.actionId, action.notification);
    });

    return () => {
      PushNotifications.removeAllListeners();
    };
  }, [setRegistrationToken, setIsRegistered, setRegistrationError]);

  return {
    registrationToken,
    registrationError,
    isRegistered,
    checkPermissions,
    registerNotifications,
    getDeliveredNotifications,
  };
}
